import { useRef, type ReactNode, type Ref } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { RecordCard } from '../../components/primitives/RecordCard';
import {
  BOARD_STAGES,
  cardsByStage,
  MOVING_CARD,
  MOVING_CARD_ID,
  type BoardStage,
  type DemoBoardCard
} from './landingBoardDemoData';
import { LandingMockPointer } from './LandingMockPointer';
import { useBoardDragLoop, type OverlayPhase } from './useBoardDragLoop';

const OVERLAY_SPRING = { type: 'spring' as const, stiffness: 420, damping: 24, mass: 0.85 };

function overlayLiftMotion(phase: OverlayPhase) {
  const lifted = phase === 'lift' || phase === 'drag';
  return {
    y: lifted ? -10 : 0,
    rotate: lifted ? 2 : 0
  };
}

function BoardCardFace({ card }: { card: DemoBoardCard }) {
  return (
    <>
      <RecordCard.Title>{card.title}</RecordCard.Title>
      <RecordCard.Meta>
        {card.chips.map((chip) => (
          <RecordCard.Chip
            key={chip}
            className="pn-record-card-chip--priority"
          >
            {chip}
          </RecordCard.Chip>
        ))}
      </RecordCard.Meta>
    </>
  );
}

function BoardCard({
  card,
  innerRef,
  isDragging
}: {
  card: DemoBoardCard;
  innerRef?: Ref<HTMLDivElement>;
  isDragging?: boolean;
}) {
  return (
    <motion.div
      ref={innerRef}
      layout={isDragging ? false : 'position'}
      data-dragging={isDragging ? 'true' : undefined}
      data-testid={card.id === MOVING_CARD_ID ? 'lp-mock-moving-card' : undefined}
    >
      <RecordCard layout="stack" dragging={isDragging}>
        <BoardCardFace card={card} />
      </RecordCard>
    </motion.div>
  );
}

function BoardColumn({
  stage,
  cards,
  colRef,
  renderCard
}: {
  stage: BoardStage;
  cards: DemoBoardCard[];
  colRef?: Ref<HTMLDivElement>;
  renderCard?: (card: DemoBoardCard) => ReactNode;
}) {
  return (
    <div
      ref={colRef}
      className="work-items-route__board-col"
      data-testid={`lp-mock-board-col-${stage}`}
    >
      <header className="work-items-route__board-col-head">
        <span className="work-items-route__board-col-name">{stage}</span>
        <span className="work-items-route__board-col-count">{cards.length}</span>
      </header>
      <div className="work-items-route__board-col-body">
        {cards.map((card) =>
          renderCard ? renderCard(card) : <BoardCard key={card.id} card={card} />
        )}
      </div>
    </div>
  );
}

export function LandingWebBoardStatic() {
  const columns = cardsByStage('Backlog');

  return (
    <div className="work-items-route__board-wrap lp-board-demo-stage">
      <div className="work-items-route__board" data-column-count={BOARD_STAGES.length}>
        {BOARD_STAGES.map((stage) => (
          <BoardColumn key={stage} stage={stage} cards={columns[stage]} />
        ))}
      </div>
    </div>
  );
}

function LandingWebBoardAnimated() {
  const stageWrapRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageWrapRef, { amount: 0.2, once: false });

  const {
    stageRef,
    movingCardRef,
    backlogColRef,
    inProgressColRef,
    movingStage,
    showCursor,
    pointerClicking,
    showOverlay,
    overlayPhase,
    isDragging,
    cursor,
    overlayBox
  } = useBoardDragLoop(inView);

  const columns = cardsByStage(movingStage);
  const lifted = overlayPhase === 'lift' || overlayPhase === 'drag';

  return (
    <div
      ref={stageWrapRef}
      className="work-items-route__board-wrap lp-board-demo-stage"
    >
      <div ref={stageRef} className="lp-board-demo-stage-inner">
        <div className="work-items-route__board" data-column-count={BOARD_STAGES.length}>
          {BOARD_STAGES.map((stage) => (
            <BoardColumn
              key={stage}
              stage={stage}
              cards={columns[stage]}
              colRef={
                stage === 'Backlog'
                  ? backlogColRef
                  : stage === 'In progress'
                    ? inProgressColRef
                    : undefined
              }
              renderCard={(card) => (
                <BoardCard
                  key={card.id}
                  card={card}
                  innerRef={card.id === MOVING_CARD_ID ? movingCardRef : undefined}
                  isDragging={card.id === MOVING_CARD_ID && isDragging}
                />
              )}
            />
          ))}
        </div>

        {showOverlay ? (
          <motion.div
            className={`lp-board-demo-overlay${lifted ? ' lp-board-demo-overlay--lifted' : ''}`}
            aria-hidden="true"
            style={{
              left: overlayBox.x,
              top: overlayBox.y,
              width: overlayBox.width,
              height: overlayBox.height
            }}
            initial={false}
            animate={overlayLiftMotion(overlayPhase)}
            transition={OVERLAY_SPRING}
          >
            <RecordCard
              layout="stack"
              className="lp-board-demo-overlay-card pn-record-card--overlay"
            >
              <BoardCardFace card={MOVING_CARD} />
            </RecordCard>
          </motion.div>
        ) : null}

        <LandingMockPointer
          visible={showCursor}
          x={cursor.x}
          y={cursor.y}
          clicking={pointerClicking}
          grabbing={isDragging}
        />
      </div>
    </div>
  );
}

export function LandingWebBoardDemo() {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) {
    return <LandingWebBoardStatic />;
  }
  return <LandingWebBoardAnimated />;
}
